import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { useEffect, useState } from 'react';
import OutlookAddIn from './OutlookAddIn';
import CircularLoader from '../main-components/loader/CircularLoader';

function OutlookLandingPage() {
  const { path } = useRouteMatch();
  const [isOutlook, setIsOutlook] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const loadOfficeJS = () => {
      return new Promise<void>((resolve, reject) => {
        if ((window as any).Office) {
          console.log('Office.js already available');
          setTimeout(() => resolve(), 500);
          return;
        }

        const existingScripts = document.querySelectorAll('script[src*="office.js"]');
        existingScripts.forEach((script) => script.remove());

        const script = document.createElement('script');
        script.src = 'https://appsforoffice.microsoft.com/lib/1/hosted/office.js';
        script.async = true;

        script.onload = () => {
          console.log('Office.js script loaded');
          setTimeout(() => resolve(), 500);
        };

        script.onerror = () => reject(new Error('Failed to load Office.js'));
        document.head.appendChild(script);
      });
    };

    const initOffice = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Load Office.js with timeout
        await Promise.race([
          loadOfficeJS(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Office.js load timeout')), 10000),
          ),
        ]);

        // Wait for Office to be ready with timeout
        const info = (await Promise.race([
          (window as any).Office.onReady(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Office.onReady timeout')), 10000),
          ),
        ])) as any;

        console.log('Office initialized. Host:', info.host);

        if (info.host === (window as any).Office.HostType.Outlook || info.host === 'Outlook') {
          setIsOutlook(true);
          setError(null);
        } else {
          setError(`Not Outlook host. Host detected: ${info.host}`);
        }
      } catch (err: any) {
        console.error('Office.js initialization failed:', err);
        setError(err.message || 'Failed to initialize Office.js');
      } finally {
        setIsLoading(false);
      }
    };

    initOffice();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <CircularLoader />
        <div>Loading Outlook Add-in...</div>
        {retryCount > 0 && (
          <div style={{ fontSize: '12px', color: '#666' }}>Retry attempt {retryCount}</div>
        )}
      </div>
    );
  }

  if (!isOutlook) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '16px',
        }}
      >
        <div>This page must be opened in Outlook.</div>
        {error && <div style={{ color: '#666', fontSize: '14px' }}>{error}</div>}
        <button
          onClick={handleRetry}
          style={{
            padding: '10px 20px',
            marginTop: '16px',
            cursor: 'pointer',
            backgroundColor: '#0062cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        >
          Retry {retryCount > 0 && `(${retryCount})`}
        </button>
      </div>
    );
  }

  return (
    <Switch>
      <Route path={path} component={OutlookAddIn} />
    </Switch>
  );
}

export default OutlookLandingPage;
